import { useCallback, useMemo, useState } from 'react';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import { ActionIcon, Button, Input, Text, Textarea, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import OrderControl from '../../../components/buttons/OrderControl';
import ModalForm from '../../../components/forms/ModalForm';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useFilters from '../../../hooks/useFilters';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { CallResultClassificationService } from '../../../services/calls/callResultClassificationService';

const initialForm = {
    classificationId: '',
    name: '',
    code: '',
    description: '',
    generatesAutomaticIncidence: false,
    requiresSchedule: false,
    isActive: true,
    displayOrder: 0,
};

const columnOrder = [
    'displayOrder',
    'name',
    'code',
    'requiresSchedule',
    'generatesAutomaticIncidence',
    'isActive',
];

const CallResultClassificationsPage = () => {
    const service = new CallResultClassificationService();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const [form, setForm] = useState(initialForm);
    const [isSaving, setIsSaving] = useState(false);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters({}, [{ field: 'displayOrder', order: 'asc' }], 1, 25);

    const [response, loading, error, refetch] = useFetch(useCallback(async () => {
        const apiResponse = await service.list(filters);

        return apiResponse.getResponseData();
    }, [filters]));

    const rows = useMemo(() => {
        if (Array.isArray(response?.data)) return response.data;
        if (Array.isArray(response)) return response;
        return [];
    }, [response]);

    const lastPage = response?.lastPage ?? 1;
    const isEditing = Boolean(form.classificationId);
    const canCreate = userCan('create_call_result_classifications', 'call_result_classifications');
    const canEdit = userCan('edit_call_result_classifications', 'call_result_classifications');
    const canDelete = userCan('delete_call_result_classifications', 'call_result_classifications');
    const canUpdateOrder = canEdit;

    const closeFormModal = () => {
        setIsFormModalOpen(false);
        setForm(initialForm);
    };

    const formIsValid = useMemo(() => (
        form.name.trim() !== ''
        && form.code.trim() !== ''
        && Number(form.displayOrder) > 0
    ), [form.code, form.displayOrder, form.name]);

    const orderedData = () => rows.map((row: any) => {
        const orderedRow: any = {
            id: row.id,
        };

        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });

        return orderedRow;
    });

    const handleSubmit = async () => {
        if (!formIsValid) {
            toast.error('Debes informar nombre, código y un orden mayor que 0.');
            return;
        }

        setIsSaving(true);

        try {
            const payload = {
                ...form,
                code: form.code.trim(),
                name: form.name.trim(),
                description: form.description.trim() || null,
                displayOrder: Number(form.displayOrder) || 0,
            };

            const apiResponse = isEditing
                ? await (await service.edit(payload)).getResponseData()
                : await (await service.create(payload)).getResponseData();

            if (!apiResponse?.success) {
                handleErrors(apiResponse);
                return;
            }

            toast.success(isEditing ? 'Tipo de seguimiento actualizado' : 'Tipo de seguimiento creado');
            closeFormModal();
            refetch();
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (classificationId: string) => {
        try {
            const apiResponse = await (await service.delete(classificationId)).getResponseData();
            if (!apiResponse?.success) {
                handleErrors(apiResponse);
                return;
            }

            if (form.classificationId === classificationId) {
                closeFormModal();
            }

            toast.success('Tipo de seguimiento eliminado');
            refetch();
        } catch (error) {
            handleErrors(error);
        }
    };

    const handleOrderChange = async (classificationId: string, action: 'up' | 'down') => {
        try {
            const apiResponse = await (await service.changeOrder(classificationId, action)).getResponseData();

            if (!apiResponse?.success) {
                handleErrors(apiResponse);
                return;
            }

            toast.success('Orden actualizado');
            refetch();
        } catch (error) {
            handleErrors(error);
        }
    };

    if (loading && !response) {
        return <Loader height="50vh" />;
    }

    if (error && !loading) {
        return (
            <Page container="fluid">
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    No se han podido cargar las tipificaciones.
                </div>
            </Page>
        );
    }

    return (
        <Page container="fluid">
            <CustomTable
                id="call-result-classifications-list-table"
                columnOrder={columnOrder}
                data={orderedData()}
                isLoading={loading}
                columnsNotShown={[]}
                overrideColumns={[
                    {
                        key: 'displayOrder',
                        render: (row: any) => (
                            <OrderControl
                                id={row.row.original.id}
                                order={row.row.original.displayOrder}
                                index={row.row.index}
                                totalRegisters={response?.totalRegisters || rows.length}
                                onOrderChange={handleOrderChange}
                                hideControls={!canUpdateOrder}
                            />
                        ),
                    },
                    {
                        key: 'name',
                        render: (row: any) => <Text className="font-bold text-primary">{row.row.original.name || 'N/A'}</Text>,
                    },
                    {
                        key: 'requiresSchedule',
                        render: (row: any) => (row.row.original.requiresSchedule ? 'Sí' : 'No'),
                    },
                    {
                        key: 'generatesAutomaticIncidence',
                        render: (row: any) => (row.row.original.generatesAutomaticIncidence ? 'Sí' : 'No'),
                    },
                    {
                        key: 'isActive',
                        render: (row: any) => (row.row.original.isActive ? 'Sí' : 'No'),
                    },
                ]}
                actions={[
                    {
                        label: 'Edit',
                        permissions: { group: 'call_result_classifications', permission: 'edit_call_result_classifications' },
                        render: (row: any) => (
                            <Tooltip size="sm" content="Editar tipo de seguimiento" placement="top" color="invert">
                                <ActionIcon
                                    as="span"
                                    size="sm"
                                    variant="outline"
                                    className={`hover:!border-gray-900 hover:text-gray-700 ${!canEdit ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
                                    onClick={() => {
                                        if (!canEdit) return;
                                        setForm({
                                            classificationId: row.id,
                                            name: row.name ?? '',
                                            code: row.code ?? '',
                                            description: row.description ?? '',
                                            generatesAutomaticIncidence: Boolean(row.generatesAutomaticIncidence),
                                            requiresSchedule: Boolean(row.requiresSchedule),
                                            isActive: Boolean(row.isActive),
                                            displayOrder: Number(row.displayOrder) || 0,
                                        });
                                        setIsFormModalOpen(true);
                                    }}
                                >
                                    <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                </ActionIcon>
                            </Tooltip>
                        ),
                    },
                    {
                        label: 'Delete',
                        permissions: { group: 'call_result_classifications', permission: 'delete_call_result_classifications' },
                        render: (row: any) => (
                            <Tooltip size="sm" content="Eliminar" placement="top" color="invert">
                                <div className={!canDelete ? 'pointer-events-none opacity-50' : ''}>
                                    <DeletePopover
                                        title="Eliminar tipo de seguimiento"
                                        description={`¿Estás seguro de que deseas eliminar ${row.name}?`}
                                        onDelete={() => handleDelete(row.id)}
                                    />
                                </div>
                            </Tooltip>
                        ),
                    },
                ]}
                filters={filters}
                updateFilters={updateFilters}
                updateFilterOrder={updateFilterOrder}
                defaultOrder={filters.filter_order || undefined}
                paginationData={{
                    pageSize: filters.limit,
                    currentPage: filters.page,
                    pageCount: lastPage,
                    totalCount: response?.totalRegisters || rows.length,
                    handlePagination: updatePage,
                    handlePerPage: updatePageSize,
                }}
                filter={false}
            >
                <div className="flex items-center gap-2">
                    {canCreate && (
                        <Button
                            onClick={() => {
                                setForm({
                                    ...initialForm,
                                    displayOrder: (response?.totalRegisters || rows.length) + 1,
                                });
                                setIsFormModalOpen(true);
                            }}
                        >
                            Nuevo tipo
                        </Button>
                    )}
                    <Button variant="outline" onClick={() => refetch()}>
                        Actualizar
                    </Button>
                </div>
            </CustomTable>

            <ModalForm
                isOpen={isFormModalOpen}
                onClose={closeFormModal}
                title={isEditing ? 'Editar tipo de seguimiento' : 'Nuevo tipo de seguimiento'}
                size="lg"
            >
                <div className="space-y-6">
                    <div className="grid gap-4 md:grid-cols-2">
                        <Input
                            label="Nombre"
                            value={form.name}
                            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                        />
                        <Input
                            label="Código"
                            value={form.code}
                            onChange={(event) => setForm((current) => ({ ...current, code: event.target.value }))}
                        />
                        <Input
                            label="Orden"
                            type="number"
                            min={1}
                            value={String(form.displayOrder)}
                            onChange={(event) => setForm((current) => ({ ...current, displayOrder: Number(event.target.value) || 0 }))}
                        />
                        <div className="flex flex-wrap items-center gap-4 pt-7">
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={form.generatesAutomaticIncidence}
                                    onChange={(event) => setForm((current) => ({ ...current, generatesAutomaticIncidence: event.target.checked }))}
                                />
                                Genera incidencia automática
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={form.requiresSchedule}
                                    onChange={(event) => setForm((current) => ({ ...current, requiresSchedule: event.target.checked }))}
                                />
                                Requiere agenda
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={form.isActive}
                                    onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))}
                                />
                                Activo
                            </label>
                        </div>
                        <div className="md:col-span-2">
                            <Textarea
                                label="Descripción"
                                value={form.description}
                                onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                                rows={4}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={closeFormModal}>
                            Cancelar
                        </Button>
                        <Button onClick={handleSubmit} disabled={isSaving || !formIsValid}>
                            {isSaving ? 'Guardando...' : (isEditing ? 'Guardar cambios' : 'Crear tipo')}
                        </Button>
                    </div>
                </div>
            </ModalForm>
        </Page>
    );
};

export default CallResultClassificationsPage;
